import React, { useMemo, useRef, useState } from "react";
import FullscreenModal from "../common/FullscreenModal/FullscreenModal";
import { Checkbox } from "@mantine/core";
import HighchartsReact from "highcharts-react-official";
import Highcharts from "highcharts/highstock";
import moment from "moment";
import "./billSummary.css";
import { useMantineColorScheme } from "@mantine/core";
import { FIELDS, FIELD_LABELS, LEGEND_FIELDS, AFTER_BAR_COLORS, COLORS } from "./billSummary.constants";
// Before bar colors: one shade per field (COLORS). After bar colors: one orange shade per field (AFTER_BAR_COLORS).
import { clamp } from "./billSummary.utils";
import { formatCompactNumber, formatEnUsValue, formatProjectDate } from "../../utils/helpers";
import { projectDetailsAtom } from "../../utils/atoms/projectDetailsAtom";
import { useAtom } from "jotai";
import { useTranslation } from "react-i18next";

const EMPTY_ARRAY = [];

const MONTH_FORMATS = [
  "MM/YYYY",
  "M/YYYY",
  "MM/YY",
  "M/YY",
  "YYYY-MM-DD",
  "YYYY/MM/DD",
  "DD/MM/YYYY",
  "MMM YYYY",
  "MMMM YYYY",
];

const formatMonthLabel = (periodStart, fallbackIndex = 0) => {
  const parsed = moment(periodStart, MONTH_FORMATS, true);
  if (parsed.isValid()) {
    return parsed.format("MMM YYYY");
  }

  const looseParsed = moment(periodStart);
  if (looseParsed.isValid()) {
    return looseParsed.format("MMM YYYY");
  }

  return moment().month(fallbackIndex % 12).format("MMM YYYY");
};

const formatYAxisLabel = function formatYAxisLabel() {
  return formatCompactNumber(this.value);
};

const formatBillSummaryTooltip = function formatBillSummaryTooltip() {
  const title = this.points?.[0]?.key || this.x;
  const lines = [`<div class="bill-summary-tooltip-title">${title}</div>`];
  (this.points || []).forEach((point) => {
    if (!Number.isFinite(point.y)) return;
    lines.push(
      `<div><span style="color:${point.color}">●</span> ${point.series.name}: <b>${formatEnUsValue(point.y)}</b></div>`,
    );
  });
  return lines.join("");
};

const handleKeyboardActivation = (event, action) => {
  if (event.key === "Enter" || event.key === " ") {
    event.preventDefault();
    action();
  }
};

const BillSummaryChart = ({ beforeMG, afterMG, inModalInstance = false }) => {
  // Line graph visibility state
  const [showBeforeDemand, setShowBeforeDemand] = useState(true);
  const [showAfterDemand, setShowAfterDemand] = useState(true);
  const [showBeforeEnergy, setShowBeforeEnergy] = useState(true);
  const [showAfterEnergy, setShowAfterEnergy] = useState(true);
  const [fullscreenModal, setFullscreenModal] = useState(false);
  const [projectDetails] = useAtom(projectDetailsAtom);
  const chartRef = useRef(null);
  const { colorScheme } = useMantineColorScheme();
  const legendTextColor = colorScheme === "dark" ? "#e0e0e0" : "#222";
  const currencyCode = (projectDetails && projectDetails.projectDTO && projectDetails.projectDTO.currencyCode) ? projectDetails.projectDTO.currencyCode : "$";
  const { t } = useTranslation();

  // Zoom state: normalized [start, end] fraction of visible months
  const [zoomRange, setZoomRange] = useState([0, 1]);

  // Always use safe arrays for calculations
  const safeBeforeMG = Array.isArray(beforeMG) ? beforeMG : EMPTY_ARRAY;
  const safeAfterMG = Array.isArray(afterMG) ? afterMG : EMPTY_ARRAY;
  const hasBefore = safeBeforeMG.length > 0;
  const hasAfter = safeAfterMG.length > 0;
  const primarySeries = hasBefore ? safeBeforeMG : safeAfterMG;

  const allowedLegendKeys = React.useMemo(() => {
    const keys = [];
    if (hasBefore) {
      keys.push(...FIELDS.map((field) => `before-${field}`));
    }
    if (hasAfter) {
      keys.push(...FIELDS.map((field) => `after-${field}`));
    }
    return keys;
  }, [hasBefore, hasAfter]);

  // Compute which fields are non-zero in both beforeMG and afterMG
  const nonZeroFields = React.useMemo(() => {
    if (!hasBefore && !hasAfter) return FIELDS;
    return FIELDS.filter((field) => {
      const allBeforeZero =
        !hasBefore ||
        safeBeforeMG.every((bm) => !bm[field] || bm[field] === 0);
      const allAfterZero =
        !hasAfter ||
        safeAfterMG.every((am) => !am[field] || am[field] === 0);
      return !(allBeforeZero && allAfterZero);
    });
  }, [safeBeforeMG, safeAfterMG, hasBefore, hasAfter]);

  // Legend check/uncheck state
  // visibleFields now only tracks checked fields, but all available fields are always shown in the legend
  const [visibleFields, setVisibleFields] = useState(() => {
    return LEGEND_FIELDS.map((f) => f.key).filter((key) => {
      const [type, field] = key.split("-");
      return nonZeroFields.includes(field) && allowedLegendKeys.includes(key);
    });
  });

  // Remove fields from visibleFields only if they become unavailable, but always show all available fields in legend
  React.useEffect(() => {
    setVisibleFields((prev) => {
      const allowed = LEGEND_FIELDS.map((f) => f.key).filter((key) => {
        const [type, field] = key.split("-");
        return nonZeroFields.includes(field) && allowedLegendKeys.includes(key);
      });
      const filtered = Array.isArray(prev) ? prev.filter((key) => allowed.includes(key)) : [];
      return filtered;
    });
  }, [nonZeroFields, allowedLegendKeys]);

  // Prepare chart data outside useEffect so it's available everywhere
  const data =
    hasBefore || hasAfter
      ? primarySeries.map((entry, i) => ({
          month: (() => {
            const parsed = moment(entry?.periodStart, ["MM/YYYY", "M/YYYY", "YYYY-MM-DD", "YYYY/MM/DD", "DD/MM/YYYY"], true);
            return parsed.isValid()
              ? formatProjectDate(parsed.toDate(), projectDetails?.projectDTO?.dateFormat)
              : (entry?.periodStart?.split("/")?.[1] || `M${i + 1}`);
          })(),
          before: FIELDS.map((f) => (hasBefore ? safeBeforeMG?.[i]?.[f] ?? 0 : 0)),
          after: FIELDS.map((f) => (hasAfter ? safeAfterMG?.[i]?.[f] ?? 0 : 0)),
        }))
      : [];

  // Determine zoom indices
  const totalMonths = data.length;
  let [zs, ze] = zoomRange;
  zs = Math.max(0, Math.min(1, zs));
  ze = Math.max(zs, Math.min(1, ze));
  let startIdx = Math.floor(zs * totalMonths);
  let endIdx = Math.ceil(ze * totalMonths);
  if (endIdx <= startIdx) endIdx = Math.min(totalMonths, startIdx + 1);
  const visibleData = data.slice(startIdx, endIdx);

  const chartOptions = useMemo(() => {
    const categories = visibleData.map((d) => d.month);
    const series = [];
    const axisGridLineColor = colorScheme === "dark" ? "#2e2e2e" : "#e6e6e6";

    FIELDS.forEach((field, i) => {
      if (hasBefore) {
        const key = `before-${field}`;
        series.push({
          id: key,
          name: `${FIELD_LABELS[field]} (${t("result.billSummary.legend.before")})`,
          type: "column",
          yAxis: 0,
          stack: "before",
          color: COLORS[i] || "#228be6",
          data: visibleData.map((d) => Number.isFinite(d?.before?.[i]) ? d.before[i] : 0),
          visible: visibleFields.includes(key),
          showInLegend: false,
          pointPadding: 0.02,
          groupPadding: 0.12,
        });
      }
      if (hasAfter) {
        const key = `after-${field}`;
        const afterBaseColor = AFTER_BAR_COLORS[i] || "#2e7d32";
        series.push({
          id: key,
          name: `${FIELD_LABELS[field]} (${t("result.billSummary.legend.after")})`,
          type: "column",
          yAxis: 0,
          stack: "after",
          color: afterBaseColor,
          data: visibleData.map((d) => Number.isFinite(d?.after?.[i]) ? d.after[i] : 0),
          visible: visibleFields.includes(key),
          showInLegend: false,
          pointPadding: 0.02,
          groupPadding: 0.12,
        });
      }
    });

    if (hasBefore) {
      series.push({
        id: "before-demand",
        name: t("result.billSummary.tooltip.beforeDemand"),
        type: "spline",
        yAxis: 1,
        color: "#0d47a1",
        data: safeBeforeMG.slice(startIdx, endIdx).map((bm) => (Number.isFinite(bm?.demand) ? bm.demand : null)),
        visible: showBeforeDemand,
        marker: { enabled: false },
        lineWidth: 2,
        showInLegend: false,
      });
      series.push({
        id: "before-energy",
        name: t("result.billSummary.tooltip.beforeEnergy"),
        type: "spline",
        yAxis: 1,
        color: "#64b5f6",
        data: safeBeforeMG.slice(startIdx, endIdx).map((bm) => (Number.isFinite(bm?.energy) ? bm.energy : null)),
        visible: showBeforeEnergy,
        marker: { enabled: false },
        lineWidth: 2,
        showInLegend: false,
      });
    }

    // Rescale the cost axis to the magnitude of the currently visible bars only,
    // so a smaller-magnitude group (e.g. only "After" checked) isn't dwarfed by a stale larger scale.
    let costAxisMax = 0;
    let costAxisMin = 0;
    visibleData.forEach((d) => {
      let beforeSum = 0;
      let afterSum = 0;
      FIELDS.forEach((field, i) => {
        if (hasBefore && visibleFields.includes(`before-${field}`)) {
          beforeSum += Number.isFinite(d.before[i]) ? d.before[i] : 0;
        }
        if (hasAfter && visibleFields.includes(`after-${field}`)) {
          afterSum += Number.isFinite(d.after[i]) ? d.after[i] : 0;
        }
      });
      costAxisMax = Math.max(costAxisMax, beforeSum, afterSum);
      costAxisMin = Math.min(costAxisMin, beforeSum, afterSum);
    });
    const costAxisRange = costAxisMax - costAxisMin;
    const costAxisPadding = costAxisRange > 0 ? costAxisRange * 0.1 : Math.abs(costAxisMax) * 0.1 || 1;
    const costAxisExtremes = costAxisMax > 0 || costAxisMin < 0
      ? { min: costAxisMin < 0 ? costAxisMin - costAxisPadding : 0, max: costAxisMax + costAxisPadding }
      : { min: undefined, max: undefined };

    if (hasAfter) {
      series.push({
        id: "after-demand",
        name: t("result.billSummary.tooltip.afterDemand"),
        type: "spline",
        yAxis: 1,
        color: "#2e7d32",
        data: safeAfterMG.slice(startIdx, endIdx).map((am) => (Number.isFinite(am?.demand) ? am.demand : null)),
        visible: showAfterDemand,
        marker: { enabled: false },
        lineWidth: 2,
        showInLegend: false,
      });
      series.push({
        id: "after-energy",
        name: t("result.billSummary.tooltip.afterEnergy"),
        type: "spline",
        yAxis: 1,
        color: "#81c784",
        data: safeAfterMG.slice(startIdx, endIdx).map((am) => (Number.isFinite(am?.energy) ? am.energy : null)),
        visible: showAfterEnergy,
        marker: { enabled: false },
        lineWidth: 2,
        showInLegend: false,
      });
    }

    return {
      chart: {
        height: 550,
        backgroundColor: colorScheme === "dark" ? "#111111" : "#fff",
        style: { fontFamily: "Nunito, sans-serif" },
      },
      title: { text: null },
      credits: { enabled: false },
      legend: { enabled: false },
      xAxis: {
        categories,
        crosshair: { width: 1, color: colorScheme === "dark" ? "#555" : "#bbb" },
        labels: { style: { color: legendTextColor } },
        title: { text: t("result.billSummary.axis.month"), style: { color: legendTextColor } },
      },
      yAxis: [
        {
          gridLineWidth: 0.5,
          gridLineColor: axisGridLineColor,
          title: { text: `Cost (${currencyCode})`, style: { color: legendTextColor } },
          min: costAxisExtremes.min,
          max: costAxisExtremes.max,
          labels: {
            style: { color: legendTextColor },
            formatter: formatYAxisLabel,
          },
        },
        {
          opposite: true,
          gridLineWidth: 0.5,
          gridLineColor: axisGridLineColor,
          title: { text: t("result.billSummary.axis.rightLabel"), style: { color: legendTextColor } },
          labels: {
            style: { color: legendTextColor },
            formatter: formatYAxisLabel,
          },
        },
      ],
      tooltip: {
        shared: true,
        useHTML: true,
        backgroundColor: colorScheme === "dark" ? "#1f1f1f" : "#ffffff",
        borderColor: colorScheme === "dark" ? "#666" : "#ccc",
        style: { color: legendTextColor },
        formatter: formatBillSummaryTooltip,
      },
      plotOptions: {
        column: {
          stacking: "normal",
          borderWidth: 0,
          borderRadius: 0,
          pointPadding: 0.02,
          groupPadding: 0.12,
        },
        series: {
          animation: false,
          turboThreshold: 0,
        },
      },
      series,
    };
  }, [
    visibleData,
    hasBefore,
    hasAfter,
    visibleFields,
    safeBeforeMG,
    safeAfterMG,
    startIdx,
    endIdx,
    showBeforeDemand,
    showAfterDemand,
    showBeforeEnergy,
    showAfterEnergy,
    colorScheme,
    legendTextColor,
    currencyCode,
    t,
  ]);

  // Zoom button overlay (like stackedBarChart)
  const zoomBtnStyle = {
    position: "absolute",
    left: 24,
    right: "auto",
    top: 8,
    display: "flex",
    gap: "8px",
    zIndex: 10,
  };

  const beforeFieldKeys = useMemo(
    () => FIELDS.filter((field) => nonZeroFields.includes(field)).map((field) => `before-${field}`),
    [nonZeroFields]
  );
  const afterFieldKeys = useMemo(
    () => FIELDS.filter((field) => nonZeroFields.includes(field)).map((field) => `after-${field}`),
    [nonZeroFields]
  );

  const beforeSelectedCount = beforeFieldKeys.filter((key) => visibleFields.includes(key)).length
    + (showBeforeDemand ? 1 : 0)
    + (showBeforeEnergy ? 1 : 0);
  const afterSelectedCount = afterFieldKeys.filter((key) => visibleFields.includes(key)).length
    + (showAfterDemand ? 1 : 0)
    + (showAfterEnergy ? 1 : 0);
  const beforeTotalCount = beforeFieldKeys.length + 2;
  const afterTotalCount = afterFieldKeys.length + 2;

  const toggleBeforeGroup = (checked) => {
    setVisibleFields((prev) => {
      const withoutBefore = prev.filter((key) => !key.startsWith("before-"));
      return checked ? [...withoutBefore, ...beforeFieldKeys] : withoutBefore;
    });
    setShowBeforeDemand(checked);
    setShowBeforeEnergy(checked);
  };

  const toggleAfterGroup = (checked) => {
    setVisibleFields((prev) => {
      const withoutAfter = prev.filter((key) => !key.startsWith("after-"));
      return checked ? [...withoutAfter, ...afterFieldKeys] : withoutAfter;
    });
    setShowAfterDemand(checked);
    setShowAfterEnergy(checked);
  };

  return (
    <>
      <div
        className="bill-summary-chart-container"
        style={inModalInstance ? { width: "100%", height: "100%" } : {}}
      >
        {/* Chart zoom wrapper */}
        <div style={{ width: "100%" }}>
          <div style={{ transformOrigin: "top left", width: "100%", minWidth: 0 }}>
            {/* Compact Legends: Before and After in single rows, smaller font, toolbar at end */}
            <div style={{ width: "100%", display: "flex", flexDirection: "row", alignItems: "flex-start", justifyContent: "center", gap: 12, padding: "8px 0 4px 0" }}>
              <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 4, alignItems: "center" }}>
                {/* Before row */}
                <div className="bill-summary-legend-row" style={{ width: "100%", justifyContent: "center", flexWrap: "wrap" }}>
                  {hasBefore && (
                    <>
                  <span className="bill-summary-legend-item" style={{ marginRight: 8 }}>
                    <Checkbox
                      checked={beforeTotalCount > 0 && beforeSelectedCount === beforeTotalCount}
                      indeterminate={beforeSelectedCount > 0 && beforeSelectedCount < beforeTotalCount}
                      onChange={(e) => toggleBeforeGroup(e.target.checked)}
                      size="xs"
                      radius="sm"
                      color="gray"
                      className="bill-summary-legend-checkbox"
                      aria-label={t("result.billSummary.legend.before")}
                    />
                    <span className="bill-summary-legend-label" style={{ color: colorScheme === "dark" ? "#e0e0e0" : "#222", fontWeight: 700 }}>{t("result.billSummary.legend.before")}</span>
                  </span>
                  {FIELDS.map((field, i) => {
                    const legendKey = `before-${field}`;
                    // Only show if field is available (nonZeroFields)
                    if (!nonZeroFields.includes(field)) return null;
                    // Use per-field COLORS for before, AFTER_BAR_COLORS for after
                    const legendColor = COLORS[i] || "#228be6";
                    return (
                      <span key={legendKey} className="bill-summary-legend-item">
                        <Checkbox
                          checked={visibleFields.includes(legendKey)}
                          onChange={(e) => {
                            setVisibleFields((v) =>
                              e.target.checked
                                ? [...v, legendKey]
                                : v.filter((f) => f !== legendKey)
                            );
                          }}
                          size="xs"
                          radius="sm"
                          color="gray"
                          className="bill-summary-legend-checkbox"
                          aria-label={FIELD_LABELS[field]}
                        />
                        <span
                          className="bill-summary-legend-color"
                          style={{ background: legendColor, cursor: "pointer" }}
                          role="button"
                          tabIndex={0}
                          onClick={() => {
                            setVisibleFields((v) =>
                              v.includes(legendKey)
                                ? v.filter((f) => f !== legendKey)
                                : [...v, legendKey]
                            );
                          }}
                          onKeyDown={(event) =>
                            handleKeyboardActivation(event, () => {
                              setVisibleFields((v) =>
                                v.includes(legendKey)
                                  ? v.filter((f) => f !== legendKey)
                                  : [...v, legendKey]
                              );
                            })
                          }
                        ></span>
                        <span
                          className="bill-summary-legend-label"
                          style={{ color: colorScheme === "dark" ? "#e0e0e0" : "#222", fontWeight: 600, cursor: "pointer" }}
                          role="button"
                          tabIndex={0}
                          onClick={() => {
                            setVisibleFields((v) =>
                              v.includes(legendKey)
                                ? v.filter((f) => f !== legendKey)
                                : [...v, legendKey]
                            );
                          }}
                          onKeyDown={(event) =>
                            handleKeyboardActivation(event, () => {
                              setVisibleFields((v) =>
                                v.includes(legendKey)
                                  ? v.filter((f) => f !== legendKey)
                                  : [...v, legendKey]
                              );
                            })
                          }
                        >{FIELD_LABELS[field]}</span>
                      </span>
                    );
                  })}
                  {/* Line graph legends for Before */}
                  <span className="bill-summary-line-legend">
                    <Checkbox
                      checked={showBeforeDemand}
                      onChange={(e) => setShowBeforeDemand(e.target.checked)}
                      size="xs"
                      radius="sm"
                      color="gray"
                      className="bill-summary-legend-checkbox"
                      aria-label={t("result.billSummary.aria.beforeDemand")}
                    />
                    <svg width="28" height="14" style={{ marginRight: 2 }}>
                      <line x1="2" y1="7" x2="26" y2="7" stroke="#0d47a1" strokeWidth="2" />
                    </svg>
                    <span
                      style={{ color: "#0d47a1", fontWeight: 600, cursor: "pointer" }}
                      role="button"
                      tabIndex={0}
                      onClick={() => setShowBeforeDemand((v) => !v)}
                      onKeyDown={(event) =>
                        handleKeyboardActivation(event, () => setShowBeforeDemand((v) => !v))
                      }
                    >Demand</span>
                  </span>
                  <span className="bill-summary-line-legend">
                    <Checkbox
                      checked={showBeforeEnergy}
                      onChange={(e) => setShowBeforeEnergy(e.target.checked)}
                      size="xs"
                      radius="sm"
                      color="gray"
                      className="bill-summary-legend-checkbox"
                      aria-label={t("result.billSummary.aria.beforeEnergy")}
                    />
                    <svg width="28" height="14" style={{ marginRight: 2 }}>
                      <line x1="2" y1="7" x2="26" y2="7" stroke="#64b5f6" strokeWidth="2" />
                    </svg>
                    <span
                      style={{ color: "#64b5f6", fontWeight: 600, cursor: "pointer" }}
                      role="button"
                      tabIndex={0}
                      onClick={() => setShowBeforeEnergy((v) => !v)}
                      onKeyDown={(event) =>
                        handleKeyboardActivation(event, () => setShowBeforeEnergy((v) => !v))
                      }
                    >Energy</span>
                  </span>
                    </>
                  )}
                </div>
                {/* After row */}
                <div className="bill-summary-legend-row" style={{ width: "100%", justifyContent: "center", flexWrap: "wrap" }}>
                  {hasAfter && (
                    <>
                  <span className="bill-summary-legend-item" style={{ marginRight: 8 }}>
                    <Checkbox
                      checked={afterTotalCount > 0 && afterSelectedCount === afterTotalCount}
                      indeterminate={afterSelectedCount > 0 && afterSelectedCount < afterTotalCount}
                      onChange={(e) => toggleAfterGroup(e.target.checked)}
                      size="xs"
                      radius="sm"
                      color="teal"
                      className="bill-summary-legend-checkbox"
                      aria-label={t("result.billSummary.legend.after")}
                    />
                    <span className="bill-summary-legend-label" style={{ color: colorScheme === "dark" ? "#e0e0e0" : "#222", fontWeight: 700 }}>{t("result.billSummary.legend.after")}</span>
                  </span>
                  {FIELDS.map((field, i) => {
                    const legendKey = `after-${field}`;
                    if (!nonZeroFields.includes(field)) return null;
                    // Use AFTER_BAR_COLORS for after
                    const legendColor = AFTER_BAR_COLORS[i] || "#2e7d32";
                    return (
                      <span key={legendKey} className="bill-summary-legend-item">
                        <Checkbox
                          checked={visibleFields.includes(legendKey)}
                          onChange={(e) => {
                            setVisibleFields((v) =>
                              e.target.checked
                                ? [...v, legendKey]
                                : v.filter((f) => f !== legendKey)
                            );
                          }}
                          size="xs"
                          radius="sm"
                          color="teal"
                          className="bill-summary-legend-checkbox"
                          aria-label={FIELD_LABELS[field]}
                        />
                        <span className="bill-summary-legend-color" style={{ background: legendColor, position: "relative", cursor: "pointer" }} role="button" tabIndex={0} onClick={() => {
                          setVisibleFields((v) =>
                            v.includes(legendKey)
                              ? v.filter((f) => f !== legendKey)
                              : [...v, legendKey]
                          );
                        }} onKeyDown={(event) => handleKeyboardActivation(event, () => {
                          setVisibleFields((v) =>
                            v.includes(legendKey)
                              ? v.filter((f) => f !== legendKey)
                              : [...v, legendKey]
                          );
                        })} />
                        <span className="bill-summary-legend-label" style={{ color: colorScheme === "dark" ? "#e0e0e0" : "#222", fontWeight: 600, cursor: "pointer" }} role="button" tabIndex={0} onClick={() => {
                          setVisibleFields((v) =>
                            v.includes(legendKey)
                              ? v.filter((f) => f !== legendKey)
                              : [...v, legendKey]
                          );
                        }} onKeyDown={(event) => handleKeyboardActivation(event, () => {
                          setVisibleFields((v) =>
                            v.includes(legendKey)
                              ? v.filter((f) => f !== legendKey)
                              : [...v, legendKey]
                          );
                        })}>{FIELD_LABELS[field]}</span>
                      </span>
                    );
                  })}
                  {/* Line graph legends for After */}
                  <span className="bill-summary-line-legend">
                    <Checkbox
                      checked={showAfterDemand}
                      onChange={(e) => setShowAfterDemand(e.target.checked)}
                      size="xs"
                      radius="sm"
                      color="teal"
                      className="bill-summary-legend-checkbox"
                      aria-label={t("result.billSummary.aria.afterDemand")}
                    />
                    <svg width="28" height="14" style={{ marginRight: 2 }}>
                      <line x1="2" y1="7" x2="26" y2="7" stroke="#2e7d32" strokeWidth="2" />
                    </svg>
                    <span
                      style={{ color: "#2e7d32", fontWeight: 600, cursor: "pointer" }}
                      role="button"
                      tabIndex={0}
                      onClick={() => setShowAfterDemand((v) => !v)}
                      onKeyDown={(event) =>
                        handleKeyboardActivation(event, () => setShowAfterDemand((v) => !v))
                      }
                    >Demand</span>
                  </span>
                  <span className="bill-summary-line-legend">
                    <Checkbox
                      checked={showAfterEnergy}
                      onChange={(e) => setShowAfterEnergy(e.target.checked)}
                      size="xs"
                      radius="sm"
                      color="teal"
                      className="bill-summary-legend-checkbox"
                      aria-label={t("result.billSummary.aria.afterEnergy")}
                    />
                    <svg width="28" height="14" style={{ marginRight: 2 }}>
                      <line x1="2" y1="7" x2="26" y2="7" stroke="#81c784" strokeWidth="2" />
                    </svg>
                    <span
                      style={{ color: "#81c784", fontWeight: 600, cursor: "pointer" }}
                      role="button"
                      tabIndex={0}
                      onClick={() => setShowAfterEnergy((v) => !v)}
                      onKeyDown={(event) =>
                        handleKeyboardActivation(event, () => setShowAfterEnergy((v) => !v))
                      }
                    >Energy</span>
                  </span>
                    </>
                  )}
                </div>
              </div>
              {/* Toolbar at right end of legend list */}
              <div className="bill-summary-toolbar" style={{ ...zoomBtnStyle, position: "static", alignSelf: "flex-start", marginLeft: 16 }}>
                <button type="button" className={`bill-summary-btn${colorScheme === "dark" ? " bill-summary-btn-dark" : ""}`}
                  aria-label="bill-summary-zoom-in"
                  title="Zoom In"
                  onClick={() => {
                    const totalMonths = Math.max(data.length, 1);
                    let [s, e] = zoomRange;
                    const rangeSpan = e - s;
                    // Allow zooming to 1/3 of a month for more detail
                    const minZoom = 1 / (totalMonths * 3);
                    if (rangeSpan <= minZoom) return;
                    const newSpan = clamp(rangeSpan / 2, minZoom, 1);
                    const center = s + rangeSpan / 2;
                    let ns = center - newSpan / 2;
                    let ne = center + newSpan / 2;
                    if (ns < 0) { ne -= ns; ns = 0; }
                    if (ne > 1) { const diff = ne - 1; ns -= diff; ne = 1; }
                    setZoomRange([ns, ne]);
                  }}
                >+
                </button>
                <button type="button" className={`bill-summary-btn${colorScheme === "dark" ? " bill-summary-btn-dark" : ""}`}
                  aria-label="bill-summary-zoom-out"
                  title="Zoom Out"
                  onClick={() => {
                    const totalMonths = Math.max(data.length, 1);
                    let [s, e] = zoomRange;
                    const rangeSpan = e - s;
                    const newSpan = clamp(rangeSpan * 2, 1 / totalMonths, 1);
                    const center = s + rangeSpan / 2;
                    let ns = center - newSpan / 2;
                    let ne = center + newSpan / 2;
                    if (ns < 0) { ne -= ns; ns = 0; }
                    if (ne > 1) { const diff = ne - 1; ns -= diff; ne = 1; }
                    setZoomRange([ns, ne]);
                  }}
                >-
                </button>
                <button type="button" className={`bill-summary-btn${colorScheme === "dark" ? " bill-summary-btn-dark" : ""}`}
                  aria-label="bill-summary-reset-zoom"
                  title="Reset Zoom"
                  onClick={() => {
                    setZoomRange([0, 1]);
                    setVisibleFields(
                      LEGEND_FIELDS.map((field) => field.key).filter((key) => {
                        const [, field] = key.split("-");
                        return nonZeroFields.includes(field) && allowedLegendKeys.includes(key);
                      })
                    );
                    setShowBeforeDemand(true);
                    setShowAfterDemand(true);
                    setShowBeforeEnergy(true);
                    setShowAfterEnergy(true);
                  }}
                >Reset
                </button>
                {!inModalInstance && (
                  <button type="button" className={`bill-summary-btn${colorScheme === "dark" ? " bill-summary-btn-dark" : ""}`}
                    aria-label="bill-summary-open-fullscreen"
                    title="Fullscreen"
                    onClick={() => setFullscreenModal(true)}
                  >⛶
                  </button>
                )}
                <select
                  className={`bill-summary-btn${colorScheme === "dark" ? " bill-summary-btn-dark" : ""}`}
                  title="Download"
                  value=""
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === 'png') {
                      const chart = chartRef.current?.chart;
                      const svgEl = chart?.container?.querySelector("svg");
                      if (!svgEl) return;

                      const copyComputedStyle = (sourceNode, targetNode) => {
                        const computed = window.getComputedStyle(sourceNode);
                        const propsToCopy = [
                          "fill",
                          "stroke",
                          "stroke-width",
                          "opacity",
                          "font-family",
                          "font-size",
                          "font-weight",
                          "text-anchor",
                        ];
                        propsToCopy.forEach((prop) => {
                          const val = computed.getPropertyValue(prop);
                          if (val) targetNode.style.setProperty(prop, val);
                        });
                      };

                      const inlineAllComputedStyles = (sourceSvg, targetSvg) => {
                        const sourceNodes = sourceSvg.querySelectorAll("*");
                        const targetNodes = targetSvg.querySelectorAll("*");
                        const n = Math.min(sourceNodes.length, targetNodes.length);
                        for (let i = 0; i < n; i++) {
                          copyComputedStyle(sourceNodes[i], targetNodes[i]);
                        }
                        copyComputedStyle(sourceSvg, targetSvg);
                      };

                      const serializer = new XMLSerializer();
                      const cloned = svgEl.cloneNode(true);

                      // Inline CSS-driven styles (especially axis ticks) so the SVG->PNG keeps them.
                      try {
                        inlineAllComputedStyles(svgEl, cloned);
                      } catch (err) {
                        // If computed-style inlining fails for any reason, still attempt export.
                        console.warn("Could not inline SVG styles for export:", err);
                      }

                      const bg = document.createElementNS("http://www.w3.org/2000/svg", "rect");
                      bg.setAttribute("x", "0");
                      bg.setAttribute("y", "0");
                      bg.setAttribute("width", chart?.chartWidth || svgEl.clientWidth || svgEl.getAttribute("width") || 1200);
                      bg.setAttribute("height", chart?.chartHeight || svgEl.clientHeight || svgEl.getAttribute("height") || 600);
                      bg.setAttribute("fill", colorScheme === "dark" ? "#111111" : "#ffffff");
                      cloned.insertBefore(bg, cloned.firstChild);
                      const svgString = serializer.serializeToString(cloned);
                      const img = new Image();
                      const canvas = document.createElement("canvas");
                      const w = chart?.chartWidth || svgEl.clientWidth || parseInt(svgEl.getAttribute("width"), 10) || 1200;
                      const h = chart?.chartHeight || svgEl.clientHeight || parseInt(svgEl.getAttribute("height"), 10) || 600;
                      canvas.width = w;
                      canvas.height = h;
                      const ctx = canvas.getContext("2d");
                      img.onload = () => {
                        ctx.drawImage(img, 0, 0);
                        canvas.toBlob((blob) => {
                          if (!blob) return;
                          const url = URL.createObjectURL(blob);
                          const a = document.createElement("a");
                          a.href = url;
                          const ts = new Date().toISOString().replace(/[:.]/g, "-");
                          a.download = `bill-summary-${ts}.png`;
                          document.body.appendChild(a);
                          a.click();
                          a.remove();
                          URL.revokeObjectURL(url);
                        });
                      };
                      img.src = "data:image/svg+xml;charset=utf-8," + encodeURIComponent(svgString);
                    } else if (value === 'csv') {
                      // Download CSV with Month and all bill fields for Before and After
                      try {
                        const rows = [];
                        // Build header: Month + available series fields + available demand/energy lines
                        const fieldLabels = FIELDS.map(f => FIELD_LABELS[f]);
                        const headerCols = [
                          t('result.billSummary.axis.month'),
                          ...(hasBefore ? fieldLabels.map(l => `${l} (${t('result.billSummary.before')})`) : []),
                          ...(hasAfter ? fieldLabels.map(l => `${l} (${t('result.billSummary.after')})`) : []),
                          ...(hasBefore ? [`${t('result.billSummary.demand')} (${t('result.billSummary.before')})`] : []),
                          ...(hasAfter ? [`${t('result.billSummary.demand')} (${t('result.billSummary.after')})`] : []),
                          ...(hasBefore ? [`${t('result.billSummary.energy')} (${t('result.billSummary.before')})`] : []),
                          ...(hasAfter ? [`${t('result.billSummary.energy')} (${t('result.billSummary.after')})`] : [])
                        ];
                        rows.push(headerCols.join(','));
                        // Build data rows - each row is a month
                        primarySeries.forEach((entry, idx) => {
                          const bm = safeBeforeMG[idx] || {};
                          const am = safeAfterMG[idx] || {};
                          const month = entry?.periodStart ? entry.periodStart.split("/")[1] : `Month ${idx + 1}`;
                          const beforeVals = hasBefore
                            ? FIELDS.map(f => bm[f] !== undefined ? bm[f] : '')
                            : [];
                          const afterVals = hasAfter
                            ? FIELDS.map(f => am[f] !== undefined ? am[f] : '')
                            : [];
                          const rowVals = [
                            month,
                            ...beforeVals,
                            ...afterVals,
                            ...(hasBefore ? [bm.demand !== undefined ? bm.demand : ''] : []),
                            ...(hasAfter ? [am.demand !== undefined ? am.demand : ''] : []),
                            ...(hasBefore ? [bm.energy !== undefined ? bm.energy : ''] : []),
                            ...(hasAfter ? [am.energy !== undefined ? am.energy : ''] : [])
                          ];
                          rows.push(rowVals.join(','));
                        });
                        const csvContent = rows.join('\n');
                        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        const ts = new Date().toISOString().replace(/[:.]/g, '-');
                        a.download = `bill-summary-${ts}.csv`;
                        a.href = url;
                        document.body.appendChild(a);
                        a.click();
                        a.remove();
                        URL.revokeObjectURL(url);
                      } catch (err) {
                        // Silent fail
                      }
                    }
                    // Reset dropdown
                    e.target.selectedIndex = 0;
                  }}
                >
                  <option value="" disabled hidden>{t('common.download')}</option>
                  <option value="png">PNG</option>
                  <option value="csv">CSV</option>
                </select>
              </div>
            </div>
            {/* Chart area: 100% */}
            <div className="bill-summary-chart-area indicator-downloadable" style={{ backgroundColor: colorScheme === "dark" ? "#111111" : "#fff" }}>
              <HighchartsReact
                highcharts={Highcharts}
                options={chartOptions}
                ref={chartRef}
                constructorType="chart"
              />
            </div>
          </div>
        </div>
      </div>
      <FullscreenModal
        open={fullscreenModal}
        onClose={() => setFullscreenModal(false)}
        title={"Bill Summary Chart"}
      >
        <div className={`bill-summary-modal-chart${colorScheme === "dark" ? " bill-summary-modal-chart-dark" : ""}`}>
          <div className="bill-summary-modal-inner">
            <BillSummaryChart
              key="modal"
              beforeMG={beforeMG}
              afterMG={afterMG}
              inModalInstance={true}
            />
          </div>
        </div>
      </FullscreenModal>
    </>
  );
};
export default BillSummaryChart;
